// --- src/redux/slices/employeeKycSlice.js ---
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Load base URL from environment
const BASE_URL = process.env.REACT_APP_API_URL;

// Get auth headers from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// POST: Upload Document
export const uploadDocument = createAsyncThunk(
  'employeeKyc/uploadDocument',
  async ({ file, docName, userId, superAdminId, status }, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('docName', docName);
      formData.append('userId', userId);
      formData.append('superAdminId', superAdminId);
      formData.append('status', status);

      const response = await axios.post(`${BASE_URL}/create-employee-kyc`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Upload failed');
    }
  }
);

// GET: Fetch Documents
export const fetchDocuments = createAsyncThunk(
  'employeeKyc/fetchDocuments',
  async ({ userId }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/list-employee-kyc?userId=${userId}`,
        getAuthHeaders()
      );
      return response.data.documents;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Fetch failed');
    }
  }
);

// DELETE: Delete Document
export const deleteDocument = createAsyncThunk(
  'employeeKyc/deleteDocument',
  async (docId, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/delete-employee-kyc/${docId}`, getAuthHeaders());
      return docId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Delete failed');
    }
  }
);

// PUT: Reject Document
export const rejectDocument = createAsyncThunk(
  'employeeKyc/rejectDocument',
  async ({ id, status = 'Rejected' }, thunkAPI) => {
    try {
      await axios.put(
        `${BASE_URL}/update-employee-kyc/${id}`,
        { status },
        getAuthHeaders()
      );
      return { id, status };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Reject failed');
    }
  }
);

// PUT: Approve Document
export const approveDocument = createAsyncThunk(
  'employeeKyc/approveDocument',
  async (id, thunkAPI) => {
    try {
      await axios.put(
        `${BASE_URL}/update-employee-kyc/${id}`,
        { status: 'Approved' },
        getAuthHeaders()
      );
      return { id, status: 'Approved' };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Approve failed');
    }
  }
);

const employeeKycSlice = createSlice({
  name: 'employeeKyc',
  initialState: {
    documents: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Upload
      .addCase(uploadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.documents = state.documents.filter(doc => doc.id !== action.payload);
      })

      // Reject
      .addCase(rejectDocument.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.documents = state.documents.map(doc =>
          doc.id === id ? { ...doc, status } : doc
        );
      })

      // Approve
      .addCase(approveDocument.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.documents = state.documents.map(doc =>
          doc.id === id ? { ...doc, status } : doc
        );
      })

      // Errors
      .addCase(rejectDocument.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(approveDocument.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default employeeKycSlice.reducer;
